import { queryClient } from '@/libs/query-client';
import { buildSigninPath } from '@/utils/return-to';
import { applyAuthHeader } from './auth-headers';
import { baseURL } from './base-url';

export class HttpError<T = unknown> extends Error {
  constructor(
    public status: number,
    public data: T,
  ) {
    super(`Request failed with status ${status}`);
    this.name = 'HttpError';
  }
}

export function isHttpError<T = unknown>(error: unknown): error is HttpError<T> {
  return error instanceof HttpError;
}

export type ErrorType<Error> = HttpError<Error>;

// 同時に複数の401が発生した場合、token refreshは1回だけ実行する
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token');

  const response = await fetch(new URL('/api/token/refresh', baseURL), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh: refreshToken }),
  });

  if (!response.ok) throw new Error('Token refresh failed');

  const data = (await response.json()) as { access: string };
  const newAccessToken = data.access;
  localStorage.setItem('accessToken', newAccessToken);
  return newAccessToken;
}

function clearAuthAndRedirect() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  queryClient.setQueryData(['auth'], { isAuthenticated: false });
  // 再ログイン後に元の画面へ戻せるよう現在地を引き継ぐ。
  // ここはハードリダイレクトなので react-router の state では持ち回れない。
  window.location.href = buildSigninPath(`${window.location.pathname}${window.location.search}`);
}

function buildHeaders(url: string, options: RequestInit): Headers {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  // 倉庫アプリと同じ認証・エンドポイントを使うため、操作の発生元をサーバーへ申告する
  headers.set('X-Hanly-Client', 'office');
  applyAuthHeader(headers, { url, method: options.method });
  return headers;
}

// orval用のカスタムインスタンス
export const customInstance = async <T>(url: string, options: RequestInit): Promise<T> => {
  const requestUrl = new URL(url, baseURL);
  const fetchWithHeaders = () =>
    fetch(requestUrl, {
      ...options,
      headers: buildHeaders(url, options),
    });

  let response = await fetchWithHeaders();

  // 401: トークンリフレッシュを試みる
  if (response.status === 401) {
    try {
      // 既にrefresh中なら同じPromiseを待つ
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
    } catch {
      clearAuthAndRedirect();
      const errorData = await response.json().catch(() => null);
      throw new HttpError(401, errorData);
    }
    response = await fetchWithHeaders();
  }

  if (response.status === 401) {
    clearAuthAndRedirect();
    const errorData = await response.json().catch(() => null);
    throw new HttpError(401, errorData);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new HttpError(response.status, errorData);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
};
